'use strict';
import mime from 'mime-types';
import {isCordovaBuild} from '../../config/constants';
import {platform} from '../../core-utilities-implementation/device-services/DeviceServicesCordova';
import {NoteState} from '../../system/enums/NoteState';
import {appendToName} from '../upload-service/FileUtilities';


const angular = require('angular');
const {element} = angular;

export class DownloadService{
  static $inject = [
    '$document',
    '$compile',
    '$rootScope',
    'NotificationService',
  ];

  constructor(
    $document,
    $compile,
    $rootScope,
    notificationService,
  ) {
    this.$document = $document;
    this.$compile = $compile;
    this.$rootScope = $rootScope;
    this.notificationService = notificationService;

    if (isCordovaBuild()) {
      this.promptDownload = this.__promptCordovaDownload.bind(this);
    } else {
      this.__downloaderElement = this.__initWeb();
      this.promptDownload = this.__promptWebDownload.bind(this);
    }
  }

  /**
   * @return {*}
   * @private
   */
  __initWeb() {
    const body = this.$document.find('body');
    const template = '<a href style="height: 0; width: 0">';
    const angularTemplate = element(template);
    const downloaderScope = this.$rootScope.$new();
    const linkFn = this.$compile(angularTemplate);
    const downloaderElement = linkFn(downloaderScope);

    //required in FF, optional for Chrome
    downloaderElement.attr('target', '_self');

    // required to prevent ionic from blocking
    downloaderElement.attr('data-tap-disabled', 'true');

    body.append(downloaderElement);

    return downloaderElement;
  }

  downloadAsTextFile(template, filename) {
    const blob = new Blob([template], {type: 'text/plain'});
    this.downloadBlob(blob, filename);
  }

  downloadBlob(blob, filename) {
    const url = (window.URL || window.webkitURL).createObjectURL(blob);
    // TODO: this may require custom handling on cordova
    this.promptDownload(url, filename);
  }

  /**
   * @param url
   * @param name
   * @private
   */
  __promptWebDownload(url, name) {
    console.log(`promptWebDownload(${url}, ${name})`);
    // force setters
    this.__downloaderElement.attr('download', name || '');
    this.__downloaderElement.attr('href', url || '');

    if (url) {
      this.__downloaderElement[0].click();
    }
    return Promise.resolve(null);
  }

  // Make sure you add the domain name to the Content-Security-Policy <meta> element.
  // var url = 'http://cordova.apache.org/static/img/cordova_bot.png';

  /**
   * @param url
   * @param filename
   * @param options
   * @private
   */
  __promptCordovaDownload(url, filename, options = {}) {
    if (!url)
      return Promise.resolve(null);

    let directory;
    if (platform.isAndroid()) {
      directory = 'file:///storage/emulated/0/';
    } else if (platform.isIOS()) {
      directory = window.cordova.file.documentsDirectory;
    } else {
      console.warn('Unsupported device');
      return Promise.resolve(null);
    }
    const mimeType = mime.lookup(filename);
    const canOpen = mimeType !== false;
    const noteId = this.notificationService.addNotification({
      name: `Downloading ${filename}`,
      completeName: `Downloaded ${filename} successfully`,
      removal: this.notificationService.REMOVAL.NEVER,
      audio: {
        [NoteState.ERROR]: true,
      },
      toasts: {
        [NoteState.ERROR]: true,
        [NoteState.COMPLETE]: canOpen && !options.autoOpen,
      },
      actions: {
        [NoteState.ERROR]: {
          className: 'button button-small button-assertive',
          label: 'Retry',
          handler: this.__promptCordovaDownload.bind(this, url, filename, options),
        },
      },
    });

    const downloadPromise = new Promise((resolve, reject) => {
      // get file system
      window.resolveLocalFileSystemURL(directory, fs => {
        // get directory
        fs.getDirectory('Download', {
          create: true,
          exclusive: false
        }, resolve, reject)
      }, reject);
    })
      .then(directory => getNumberedFilename(directory, filename))
      .then(({updatedFilename, fileURL}) => this.__download(updatedFilename, fileURL, url, mimeType, noteId, options));

    this.notificationService.updateNotificationPromise(noteId, downloadPromise);

    return downloadPromise;
  }

  __download(updatedFilename, fileURL, uri, mimeType, noteId, options) {
    const canOpen = mimeType !== false;
    const openFileAction = DownloadService.__makeOpenFileAction(fileURL, mimeType);
    const completeName = `Downloaded ${updatedFilename} successfully`;

    if (canOpen && !options.autoOpen && this.notificationService.notificationExists(noteId)) {
      const existingNote = this.notificationService.getNotification(noteId);
      this.notificationService.updateNotification(noteId, {
        completeName,
        actions: {
          ...existingNote.actions,
          [NoteState.COMPLETE]: {
            className: 'button button-small button-balanced',
            label: 'Open',
            handler: openFileAction,
          },
        },
      });
    } else {
      const existingNote = this.notificationService.getNotification(noteId);
      if (existingNote.completeName !== completeName) {
        this.notificationService.updateNotification(noteId, {
          completeName,
        });
      }
    }

    // TODO: add notification with actions
    // retry should probably start all the way over and recalculate getNumberedFilename

    return new Promise((resolve, reject) => {
      const fileTransfer = new window.FileTransfer();

      fileTransfer.download(
        uri,
        fileURL,
        resolve,
        reject,
        /* trustAllHosts: */ false,
        /* options: */ {
          // headers: {},
        },
      );
    })
      .then(entry => canOpen && options.autoOpen ? openFileAction() : null)
      .catch(error => {
        console.log("download error source " + error.source);
        console.log("download error target " + error.target);
        console.log("upload error code" + error.code);
        // TODO: log error to server
      });
  }

  static __makeOpenFileAction(fileURL, mimeType) {
    return () => new Promise((resolve, reject) =>
      window.cordova.plugins.fileOpener2.open(fileURL, mimeType, {error: reject, success: resolve})
    );
  }
}

/**
 * Recursively iterate through file names until we find a number to append that does not already exist.
 * @param directory
 * @param filename
 * @param currentNumber
 * @return {*}
 */
function getNumberedFilename(directory, filename, currentNumber = 0) {
  const modifiedFilename = currentNumber > 0 ?  appendToName(filename, ` (${currentNumber})`) : filename;

  return doesFileExist(directory, modifiedFilename)
    .then(exists => {
      if (!exists) {
        return {
          updatedFilename: modifiedFilename,
          fileURL: `${directory.toURL()}${modifiedFilename}`,
        };
      } else {
        return getNumberedFilename(directory, filename, currentNumber + 1);
      }
    });
}

function doesFileExist(directory, filename) {
  return new Promise((resolve, reject) =>
    directory.getFile(filename, { create: false }, () => resolve(true), () => resolve(false))
  )
}



// WEBPACK FOOTER //
// ./src/js/core-utilities/file-dialog/DownloadService.js